import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Plus, Minus } from "lucide-react";
import { Reveal, SectionLabel } from "./Reveal";

const pillars = [
  {
    n: "01",
    title: "Mindset",
    short: "The operating system beneath every decision.",
    detail:
      "We rebuild your relationship with uncertainty, patience and self trust. Before any setup, the trader must be able to think clearly under pressure, mindset is the foundation everything else is built on.",
    points: ["Identity & self belief", "Focus and clarity under pressure", "Long term thinking over quick wins"],
  },
  {
    n: "02",
    title: "Risk Management",
    short: "Protect your money first. Always.",
    detail:
      "How much to risk, when to size up and when to protect your account, the habits that keep you in the game long enough to succeed. Protecting your money always comes before chasing profit.",
    points: ["A defined risk on every trade", "Protecting your account", "Preparing for funded accounts"],
  },
  {
    n: "03",
    title: "Professional Trading",
    short: "Read the market with clarity.",
    detail:
      "A clear, repeatable way to read price and the story behind it. We teach you how to spot high quality opportunities and exactly when to act, without the noise or the confusing jargon.",
    points: ["Reading price with confidence", "Knowing when and how to enter", "A repeatable trading framework"],
  },
  {
    n: "04",
    title: "Trading Psychology",
    short: "Master the trader within.",
    detail:
      "Staying calm under pressure, understanding your habits and building a routine. We install the mindset that lets you follow your plan when fear and greed are loudest.",
    points: ["Handling losses without spiralling", "Journaling & self review", "Preparing your mind before each session"],
  },
  {
    n: "05",
    title: "Consistency",
    short: "The compounding force.",
    detail:
      "Discipline measured over hundreds of decisions, not one. We build the accountability and process that turn scattered results into a repeatable standard.",
    points: ["Process over outcome", "Accountability & standards", "Measured, compounding progress"],
  },
];

export default function Method() {
  const [active, setActive] = useState(0);

  return (
    <section
      id="method"
      className="relative py-24 sm:py-32 lg:py-40 border-t border-white/5"
      data-testid="method-section"
    >
      <div className="max-w-5xl mx-auto px-6 lg:px-8">
        <Reveal>
          <SectionLabel>The MBM Method</SectionLabel>
        </Reveal>
        <Reveal delay={0.1}>
          <h2 className="mt-6 font-heading text-3xl sm:text-4xl lg:text-5xl font-light tracking-tight max-w-2xl">
            Five pillars. One disciplined trader.
          </h2>
        </Reveal>

        <div className="mt-16 border-t border-white/10">
          {pillars.map((p, i) => {
            const open = active === i;
            return (
              <Reveal key={p.title} delay={i * 0.05}>
                <div className="border-b border-white/10" data-testid={`method-pillar-${i}`}>
                  <button
                    onClick={() => setActive(open ? -1 : i)}
                    className="w-full flex items-center gap-6 py-7 text-left group"
                    data-testid={`method-pillar-toggle-${i}`}
                  >
                    <span
                      className="font-mono text-xs transition-colors"
                      style={{ color: open ? "#6ea8ff" : "#52525b" }}
                    >
                      {p.n}
                    </span>
                    <span className="flex-1">
                      <span
                        className="font-heading text-2xl sm:text-3xl font-light transition-colors"
                        style={{ color: open ? "#FAFAFA" : "#d4d4d8" }}
                      >
                        {p.title}
                      </span>
                      <span className="block mt-1 text-sm text-zinc-500 font-light">{p.short}</span>
                    </span>
                    <span className="text-zinc-500 group-hover:text-white transition-colors">
                      {open ? <Minus size={18} /> : <Plus size={18} />}
                    </span>
                  </button>

                  <AnimatePresence initial={false}>
                    {open && (
                      <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: "auto", opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
                        className="overflow-hidden"
                      >
                        <div className="pb-8 pl-12 grid sm:grid-cols-2 gap-6">
                          <p className="text-sm lg:text-base text-zinc-400 font-light leading-relaxed">
                            {p.detail}
                          </p>
                          <ul className="space-y-2">
                            {p.points.map((pt) => (
                              <li key={pt} className="flex items-center gap-3 text-sm text-zinc-300 font-light">
                                <span className="h-1 w-1 rounded-full bg-[#6ea8ff]" />
                                {pt}
                              </li>
                            ))}
                          </ul>
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              </Reveal>
            );
          })}
        </div>
      </div>
    </section>
  );
}
